import { useEffect } from 'react';
import type React from 'react';

import { navigate } from '@xing-com/crate-core-router';
import { XING_JOBS_SEARCH_PATH } from '@xing-com/crate-core-routes';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import { useSearchParameters } from '@xing-com/crate-hooks-use-search-parameters';
import {
  SEARCH_ALERTS_MODAL_PARAM,
  SEARCH_ALERTS_MODAL_TYPES,
  SEARCH_QUERY_ID_PARAM,
  SMALL_SCREEN_BOTTOM_SHEET_PARAM,
} from '@xing-com/crate-jobs-constants';
import { SearchMetadata } from '@xing-com/crate-jobs-domain-search-components-metadata';
import { SerpsMetadata } from '@xing-com/crate-jobs-domain-serps-components-metadata';
import type { SerpsRouteParams } from '@xing-com/crate-jobs-domain-serps-helpers';
import { getJobsSerpsSearchParameters } from '@xing-com/crate-jobs-domain-serps-helpers';
import { useSerpsMatch } from '@xing-com/crate-jobs-domain-serps-hooks';
import {
  useCurrentSearchAlertStatus,
  useIsAiSearch,
  useMarkSearchAlertAsVisited,
  useSearchState,
} from '@xing-com/crate-jobs-hooks';
import { useSearchTracking } from '@xing-com/crate-jobs-tracking';
import {
  SearchAlertsBanner,
  SearchAlertsErrorBanner,
  SearchAlertsSearchModal,
} from '@xing-com/crate-jobs-xinglets';

import { increaseSearchCount, isVisibleJob } from '../../helpers';
import { useSearch } from '../../hooks';
import { AppliedFilters } from '../shared';

import { AIFeedback } from './ai-feedback';
import { EmptyState } from './empty-state';
import { ErrorState } from './error-state';
import { transformResultsList } from './helpers';
import { LazyLoader } from './lazy-loader';
import { NotEnoughProfileDataBanner } from './not-enough-profile-data-banner';
import { ResultsHeader } from './results-header';
import { ResultsList } from './results-list';
import { ResultsSkeleton } from './results-skeleton';
import * as S from './results.styles';
import { SearchAlertsEmailBanner } from './search-alerts-email-banner';

const REPETITION_TRIGGERS = [3, 9, 16];

type LayoutWithMetadata = {
  children: React.ReactNode;
  serps: SerpsRouteParams | null;
  jobCount?: number;
};

const ResultsLayoutWithMetadata: React.FC<LayoutWithMetadata> = ({
  children,
  serps,
  jobCount,
}) => (
  <div>
    {serps ? (
      <SerpsMetadata {...serps} jobCount={jobCount} />
    ) : (
      <SearchMetadata />
    )}
    {children}
  </div>
);

type Props = {
  isStickyHeader?: boolean;
};

export const Results: React.FC<Props> = ({ isStickyHeader }) => {
  const isAiSearch = useIsAiSearch();
  const { isLoggedOut } = useLoginState();
  const serps = useSerpsMatch();
  const { trackPageview } = useSearchTracking();
  const { getSearchParameter } = useSearchParameter();
  const { setSearchParameters } = useSearchParameters();
  const {
    searchAlertId,
    isLoading: isSearchAlertsLoading,
    isSearchIdAnAlert,
  } = useCurrentSearchAlertStatus();
  const { filterCount, page, sort, since, sincePeriod, isEmptySearch } =
    useSearchState();
  const { isLoading, results, refetch, fetchMore } = useSearch();

  useMarkSearchAlertAsVisited(searchAlertId);

  const isFilterView = !!getSearchParameter(SMALL_SCREEN_BOTTOM_SHEET_PARAM);
  const newSearchId = results?.searchQuery.guid;

  useEffect(() => {
    if (isSearchAlertsLoading) {
      return;
    }

    if (newSearchId && !isFilterView) {
      trackPageview({
        results,
        sort,
        page,
        filterCount,
        shouldTrackNwt: true,
      });
    }

    if (serps) {
      const queryParameters = getJobsSerpsSearchParameters(serps);

      if (!isLoggedOut) {
        navigate(
          `${XING_JOBS_SEARCH_PATH}?${new URLSearchParams(queryParameters).toString()}`,
          { replace: true }
        );
      } else {
        setSearchParameters(queryParameters, { replace: true });
      }
      return;
    }

    if (!newSearchId) {
      return;
    }

    const keywords = results.searchQuery.body?.keywords;
    const location = results.searchQuery.body?.location;
    const canSearchBeSaved = !!(keywords || location);
    const repetitionCount = increaseSearchCount(newSearchId);
    const isActiveSA = isSearchIdAnAlert(newSearchId);

    const shouldShowRepeatedModal =
      canSearchBeSaved &&
      !isActiveSA &&
      REPETITION_TRIGGERS.includes(repetitionCount) &&
      !isFilterView;

    const queryParameters = {
      ...(shouldShowRepeatedModal
        ? { [SEARCH_ALERTS_MODAL_PARAM]: SEARCH_ALERTS_MODAL_TYPES.REPEAT }
        : {}),
      // We add the search alert query to the URL, so other components (i.e. search alerts)
      // can access it without the need of using the useSearch hook
      [SEARCH_QUERY_ID_PARAM]: newSearchId,
    };

    setSearchParameters(queryParameters, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSearchId, isSearchAlertsLoading, sort, sincePeriod]);

  if (isLoading) {
    return (
      <ResultsLayoutWithMetadata serps={serps}>
        <ResultsSkeleton />
      </ResultsLayoutWithMetadata>
    );
  }
  if (!results?.collection) {
    return (
      <ResultsLayoutWithMetadata serps={serps}>
        <ErrorState refetch={refetch} />
      </ResultsLayoutWithMetadata>
    );
  }

  const filteredCollection = results.collection.filter(isVisibleJob);
  if (filteredCollection.length === 0) {
    return (
      <ResultsLayoutWithMetadata serps={serps}>
        <NotEnoughProfileDataBanner />
        <SearchAlertsEmailBanner />
        <EmptyState serps={serps} hasSinceFilter={!!since} />
      </ResultsLayoutWithMetadata>
    );
  }

  const { items, urgentJobsHighlighted } =
    transformResultsList(filteredCollection);
  const trackingToken = filteredCollection[0].trackingToken.split('.')[0];
  const jobCount = results.boundedTotal?.total || results.total;

  return (
    <ResultsLayoutWithMetadata serps={serps} jobCount={jobCount}>
      <NotEnoughProfileDataBanner />
      <SearchAlertsEmailBanner />
      <ResultsHeader
        jobCount={jobCount}
        serps={serps}
        boundedTotals={results.boundedTotal}
        isStickyHeader={isStickyHeader}
      />
      <S.MediaConfined>
        <AppliedFilters />
      </S.MediaConfined>
      <ResultsList
        items={items}
        type="SEARCH"
        urgentJobsHighlighted={urgentJobsHighlighted}
      />
      {isAiSearch ? (
        <AIFeedback
          searchId={newSearchId}
          trackingToken={trackingToken}
          isStickyHeader={isStickyHeader}
          isEmptySearch={isEmptySearch}
        />
      ) : null}
      <LazyLoader
        isSerps={Boolean(serps && isLoggedOut)}
        hasSinceFilter={!!since}
        fetchMore={fetchMore}
        results={results}
      />
      {isAiSearch ? (
        <>
          <SearchAlertsSearchModal />
          <SearchAlertsBanner />
          <SearchAlertsErrorBanner />
        </>
      ) : null}
    </ResultsLayoutWithMetadata>
  );
};
